/**
 * Tower XP — depth, per tower type (docs 05 §soft gate, 08 §Tower XP).
 *
 * Paths A and B tiers 1–2 are always available once the tower is unlocked.
 * Everything above that is bought with the tower's own persistent XP:
 *   L2 (100) A-T3 · L3 (300) B-T3 · L4 (700) C tiers 1–2 · L5 (1500) C-T3.
 */

import { tierCaps } from '../sim/defs.ts';
import { XP_RATES } from './tree.ts';

export { tierCaps };

export const MAX_LEVEL = XP_RATES.levelThresholds.length + 1;

export function levelOf(xp: number): number {
  let level = 1;
  for (const t of XP_RATES.levelThresholds) if (xp >= t) level++;
  return level;
}

/** XP still needed for the next level, or null at max. */
export function toNextLevel(xp: number): number | null {
  const next = XP_RATES.levelThresholds[levelOf(xp) - 1];
  return next === undefined ? null : next - xp;
}

/** Why a path is closed, for the upgrade panel's blocked line. */
export function gateReason(path: 'a' | 'b' | 'c', tier: number, level: number): string | null {
  const caps = tierCaps(level);
  if (tier < caps[path]) return null;
  if (path === 'c' && caps.c === 0) return `LEVEL 4 UNLOCKS PATH C (${XP_RATES.levelThresholds[2]!} XP)`;
  const need = path === 'a' ? 2 : path === 'b' ? 3 : 5;
  if (tier >= 3) return null; // capped by the pick-2-cap-1 rule, not by XP
  return `LEVEL ${need} UNLOCKS TIER ${tier + 1} (${XP_RATES.levelThresholds[need - 2]!} XP)`;
}

/**
 * One wave's XP for one tower type: damage dealt plus a flat participation
 * bonus when it got at least one kill. Catch-up (+50%) applies to a tower below
 * the player's average level — "we believe in you; statistically, someone has to".
 */
export function waveXp(damage: number, killed: boolean, catchUp: boolean): number {
  const raw = damage * XP_RATES.perDamage + (killed ? XP_RATES.perWave : 0);
  return Math.round(raw * (catchUp ? 1 + XP_RATES.catchUpBonus : 1));
}

export function averageLevel(towerXp: Record<string, number>): number {
  const levels = Object.values(towerXp).map(levelOf);
  if (levels.length === 0) return 1;
  return levels.reduce((a, b) => a + b, 0) / levels.length;
}
